<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NButton, NEmpty, NSpin, NResult, NCard, NRate, NInput, NDivider, NAvatar, NPagination } from 'naive-ui';
import { fetchProviderReviews, submitProviderResponse, fetchProviderReviewSummary } from '../services/review';
import { getProvider } from '../services/provider';
import { authToken, isAuthenticated, currentUser } from "@/services/auth";

const route = useRoute();
const router = useRouter();
const providerId = route.params.id;

// State variables
const reviewsData = ref([]);
const loading = ref(true);
const error = ref(null);
const provider = ref(null);
const responseText = ref('');
const selectedReviewId = ref(null);
const showResponseInput = ref(false);
const submittingResponse = ref(false);

// Pagination state
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

// Review summary state
const reviewSummary = ref(null);

// Computed properties
const isCurrentUserTheProvider = computed(() => {
  if (!currentUser.value || !provider.value) return false;
  return currentUser.value.id === provider.value.userId;
});

const averageRating = computed(() => {
  if (reviewSummary.value && reviewSummary.value.average_rating) {
    return Number(reviewSummary.value.average_rating).toFixed(1);
  }
  return '0.0';
});

const totalReviewCount = computed(() => {
  if (reviewSummary.value && reviewSummary.value.total_reviews) {
    return reviewSummary.value.total_reviews;
  }
  return totalItems.value;
});

// Load data when component mounts
onMounted(async () => {
  // Get page from URL if present
  if (route.query.page) {
    currentPage.value = parseInt(route.query.page) || 1;
  }
  
  await Promise.all([loadProviderData(), loadReviewSummary(), loadReviews()]);
});

// Function to load provider data
async function loadProviderData() {
  try {
    const language = localStorage.getItem('preferredLanguage') || 'english';
    provider.value = await getProvider(providerId, language);
  } catch (err) {
    console.error('Failed to load provider:', err);
    error.value = 'Failed to load provider details.';
  }
}

// Function to load review summary
async function loadReviewSummary() {
  try {
    reviewSummary.value = await fetchProviderReviewSummary(providerId);
  } catch (err) {
    console.error('Failed to load review summary:', err);
    // Don't set error here, as reviews can still load
  }
}

// Function to load provider reviews
async function loadReviews() {
  try {
    loading.value = true;
    error.value = null;

    const language = localStorage.getItem('preferredLanguage') || 'english';
    const response = await fetchProviderReviews(
      providerId,
      currentPage.value,
      pageSize.value,
      language
    );

    reviewsData.value = response.reviews || [];

    // Update pagination data
    if (response.pagination) {
      totalItems.value = response.pagination.total;
      totalPages.value = response.pagination.total_pages;
      currentPage.value = response.pagination.page;
      pageSize.value = response.pagination.page_size;
    }

  } catch (err) {
    console.error('Failed to load reviews:', err);
    error.value = 'Failed to load reviews. Please try again later.';
  } finally {
    loading.value = false;
  }
}

// Handle page change
const handlePageChange = (page) => {
  currentPage.value = page;
  
  // Update URL with new page
  router.replace({ query: { page } });
  
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  // Load reviews for the new page
  loadReviews();
};

// Function to handle provider response
async function handleProviderResponse(reviewId) {
  if (showResponseInput.value && selectedReviewId.value === reviewId) {
    // Submit the response
    if (!responseText.value.trim()) {
      return; // Don't submit empty responses
    }

    submittingResponse.value = true;
    try {
      await submitProviderResponse(authToken.value, reviewId, responseText.value);
      // Refresh the reviews to show the new response
      await loadReviews();
      // Reset the response UI
      responseText.value = '';
      showResponseInput.value = false;
      selectedReviewId.value = null;
    } catch (err) {
      console.error('Failed to submit response:', err);
      error.value = `Failed to submit response: ${err.message}`;
    } finally {
      submittingResponse.value = false;
    }
  } else {
    // Show the response input
    selectedReviewId.value = reviewId;
    showResponseInput.value = true;
  }
}

// Function to toggle response input visibility
function toggleResponseInput(reviewId) {
  if (showResponseInput.value && selectedReviewId.value === reviewId) {
    showResponseInput.value = false;
    selectedReviewId.value = null;
  } else {
    selectedReviewId.value = reviewId;
    showResponseInput.value = true;
    responseText.value = '';
  }
}

// Format date for display
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Get user initials for avatar
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Get random pastel color for avatar background
function getAvatarColor(name) {
  if (!name) return '#8E8E8E';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 80%)`;
}

// Go back to provider page
function goBack() {
  router.push(`/providers/${providerId}`);
}
</script>

<template>
  <div class="reviews-container">
    <!-- Back button -->
    <div class="back-navigation">
      <NButton quaternary @click="goBack" class="back-button">
        <template #icon>
          <div class="back-icon">←</div>
        </template>
        Back to Provider
      </NButton>
    </div>

    <!-- Provider context section -->
    <div v-if="provider" class="provider-context">
      <div class="provider-avatar">
        <NAvatar 
          :style="{ backgroundColor: getAvatarColor(provider.provider_name) }" 
          :size="56" 
          round
        >
          {{ getInitials(provider.provider_name) }}
        </NAvatar>
      </div>
      <div class="provider-info">
        <h1 class="provider-name">{{ provider.provider_name }}</h1>
        <div class="provider-specializations" v-if="provider.specializations && provider.specializations.length">
          <span v-for="(specialization, index) in provider.specializations" 
                :key="index" 
                class="specialization-tag">
            {{ specialization }}
          </span>
        </div>
      </div>
    </div>

    <div class="reviews-header">
      <h2 class="section-title">Reviews</h2>
      
      <div v-if="!loading && totalReviewCount > 0" class="reviews-summary">
        <div class="average-rating">
          <NRate readonly :value="Number(averageRating)" allow-half />
          <span class="rating-number">{{ averageRating }}</span>
        </div>
        <div class="review-count">{{ totalReviewCount }} {{ totalReviewCount === 1 ? 'review' : 'reviews' }}</div>
      </div>
    </div>

    <NDivider />

    <!-- Results Summary -->
    <div v-if="!loading && reviewsData.length > 0" class="results-summary">
      <p>
        Showing {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} reviews
      </p>
    </div>

    <!-- Loading state -->
    <NSpin v-if="loading" class="flex justify-center py-12">
      <template #description>Loading reviews...</template>
    </NSpin>

    <!-- Error state -->
    <NResult v-else-if="error" status="error" title="Error Loading Reviews" :description="error">
      <template #footer>
        <NButton @click="loadReviews">Try Again</NButton>
      </template>
    </NResult>

    <!-- Empty state -->
    <NEmpty v-else-if="reviewsData.length === 0" description="No reviews available for this provider." />

    <!-- Reviews list -->
    <div v-else>
      <div class="reviews-list">
        <NCard v-for="item in reviewsData" :key="item.id" class="review-card">
          <div class="review-header">
            <div class="reviewer-info">
              <NAvatar :style="{ backgroundColor: getAvatarColor(item.reviewer_name) }" round>
                {{ getInitials(item.reviewer_name) }}
              </NAvatar>
              <div>
                <h3 class="reviewer-name">{{ item.reviewer_name || 'Anonymous' }}</h3>
                <div class="review-date">{{ formatDate(item.created_at) }}</div>
              </div>
            </div>
            <div class="overall-rating">
              <NRate readonly :value="item.rating" />
            </div>
          </div>

          <div class="review-content">
            <div v-if="item.review_text" class="review-text">
              <div class="review-text-content">
                <span class="quote-mark">"</span>
                {{ item.review_text }}
                <span class="quote-mark">"</span>
              </div>
            </div>

            <div class="ratings-detail">
              <div v-if="item.communication_rating" class="rating-item">
                <span class="rating-label">Communication</span>
                <div class="rating-value">
                  <NRate readonly size="small" :value="item.communication_rating" />
                  <span>{{ item.communication_rating }}.0</span>
                </div>
              </div>
              <div v-if="item.knowledge_rating" class="rating-item">
                <span class="rating-label">Knowledge</span>
                <div class="rating-value">
                  <NRate readonly size="small" :value="item.knowledge_rating" />
                  <span>{{ item.knowledge_rating }}.0</span>
                </div>
              </div>
              <div v-if="item.timeliness_rating" class="rating-item">
                <span class="rating-label">Timeliness</span>
                <div class="rating-value">
                  <NRate readonly size="small" :value="item.timeliness_rating" />
                  <span>{{ item.timeliness_rating }}.0</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Provider response section (if exists) -->
          <div v-if="item.provider_response" class="provider-response">
            <div class="provider-response-header">
              <NAvatar v-if="provider?.name" :style="{ backgroundColor: '#E6F7FF' }" round>
                <span style="color: #1890ff;">{{ getInitials(provider.name) }}</span>
              </NAvatar>
              <div>
                <div class="provider-label">Provider Response</div>
                <div class="provider-name">{{ provider?.name || 'Provider' }}</div>
              </div>
            </div>
            <div class="provider-response-text">
              {{ item.provider_response }}
            </div>
          </div>

          <!-- Provider response button & input (only shown to the claimed provider) -->
          <div
            v-if="isAuthenticated && isCurrentUserTheProvider && provider && provider.status === 'verified' && !item.provider_response"
            class="response-actions">
            <NButton v-if="!showResponseInput || selectedReviewId !== item.id" type="primary" size="small"
              @click="toggleResponseInput(item.id)">
              Respond to Review
            </NButton>

            <div v-if="showResponseInput && selectedReviewId === item.id" class="response-input-container">
              <NInput 
                v-model:value="responseText" 
                type="textarea" 
                placeholder="Write your professional response to this review. This will be publicly visible to all users." 
                :rows="4" 
              />
              <div class="response-buttons">
                <NButton type="primary" @click="handleProviderResponse(item.id)"
                  :loading="submittingResponse" :disabled="!responseText.trim()">
                  Submit Response
                </NButton>
                <NButton quaternary @click="toggleResponseInput(item.id)">
                  Cancel
                </NButton>
              </div>
            </div>
          </div>
        </NCard>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-section">
        <NPagination
          v-model:page="currentPage"
          :page-count="totalPages"
          :page-size="pageSize"
          :item-count="totalItems"
          show-size-picker
          :page-sizes="[10, 20, 50]"
          @update:page="handlePageChange"
          @update:page-size="(size) => {
            pageSize = size;
            currentPage = 1;
            loadReviews();
          }"
        >
          <template #prefix="{ itemCount }">
            Total: {{ itemCount }}
          </template>
        </NPagination>
      </div>
    </div>
  </div>
</template>

<style scoped>
.reviews-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

/* Back Navigation */
.back-navigation {
  margin-bottom: 1.5rem;
}

.back-button {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.back-icon {
  margin-right: 0.5rem;
  font-size: 1.25rem;
}

/* Provider Context */
.provider-context {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #F8FAFC;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.provider-avatar {
  margin-right: 1.25rem;
}

.provider-info {
  flex: 1;
}

.provider-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: #111827;
}

.provider-specializations {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.specialization-tag {
  background-color: #E0F2FE;
  color: #0369A1;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
}

.reviews-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .reviews-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.reviews-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.average-rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rating-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.review-count {
  font-size: 0.875rem;
  color: #6B7280;
}

.results-summary {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.95rem;
}

.results-summary p {
  margin: 0;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
}

.review-card {
  border-radius: 12px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.25rem;
}

.review-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.reviewer-info {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.reviewer-name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.review-date {
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

.review-content {
  margin-bottom: 1.5rem;
}

.review-text {
  margin-bottom: 1.25rem;
}

.review-text-content {
  position: relative;
  font-size: 1rem;
  line-height: 1.6;
  color: #4B5563;
  white-space: pre-line;
  padding: 0.75rem 1.5rem;
  background-color: #F9FAFB;
  border-radius: 8px;
  font-style: italic;
  border-left: 3px solid #D1D5DB;
}

.quote-mark {
  font-size: 1.5rem;
  line-height: 0;
  color: #9CA3AF;
  font-family: serif;
  vertical-align: middle;
  display: inline;
}

.quote-mark:first-child {
  margin-right: 0.25rem;
  vertical-align: top;
}

.quote-mark:last-child {
  margin-left: 0.25rem;
  vertical-align: bottom;
}

.ratings-detail {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}

@media (min-width: 640px) {
  .ratings-detail {
    grid-template-columns: repeat(3, 1fr);
  }
}

.rating-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.rating-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4B5563;
}

.rating-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.provider-response {
  background-color: #F0F9FF;
  border-radius: 10px;
  padding: 1.25rem;
  margin-top: 1.25rem;
  border-left: 4px solid #0EA5E9;
}

.provider-response-header {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;
}

.provider-label {
  font-weight: 600;
  color: #0284C7;
  font-size: 0.875rem;
}

.provider-response-text {
  font-size: 0.975rem;
  line-height: 1.6;
  color: #1F2937;
  white-space: pre-line;
}

.response-actions {
  margin-top: 1.25rem;
}

.response-input-container {
  margin-top: 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 1rem;
  background-color: #F9FAFB;
}

.response-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  justify-content: flex-end;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .pagination-section {
    padding: 1rem 0;
  }
}
</style>